import { useEffect, useState } from 'react';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

interface ApprovalSettingsStore {
  /** Whether to allow approving tokens without having sufficient balance */
  allowApproveWithoutBalance: boolean;
  /** Sets whether to allow approving without balance */
  setAllowApproveWithoutBalance: (allow: boolean) => void;
  /** Whether to request max (unlimited) allowance on approval. When false, approve only the exact amount needed for the swap. */
  maxApproval: boolean;
  /** Sets whether to request max allowance on approval */
  setMaxApproval: (maxApproval: boolean) => void;
}

const useInternalApprovalSettingsStore = createWithEqualityFn<ApprovalSettingsStore>()(
  persist(
    (set) => ({
      allowApproveWithoutBalance: false,
      setAllowApproveWithoutBalance: (allow: boolean) => set({ allowApproveWithoutBalance: allow }),
      maxApproval: true,
      setMaxApproval: (maxApproval: boolean) => set({ maxApproval }),
    }),
    {
      name: 'matcha-approval-settings-storage',
      skipHydration: true,
    },
  ),
  shallow,
);

/**
 * Hook to access approval settings store.
 * Ensures the zustand store is only hydrated after the initial render cycle
 * to prevent hydration mismatches.
 */
export function useApprovalSettingsStore<T>(
  selector: (state: ApprovalSettingsStore) => T = (x) => x as T,
) {
  const [rehydrated, setIsRehydrated] = useState(false);

  useEffect(() => {
    if (!rehydrated) {
      useInternalApprovalSettingsStore.persist.rehydrate();
      setIsRehydrated(true);
    }
  }, [rehydrated]);

  return useInternalApprovalSettingsStore(selector);
}

// Selectors
export const approvalSettingsSelector = (state: ApprovalSettingsStore) => ({
  allowApproveWithoutBalance: state.allowApproveWithoutBalance,
  setAllowApproveWithoutBalance: state.setAllowApproveWithoutBalance,
  maxApproval: state.maxApproval,
  setMaxApproval: state.setMaxApproval,
});

export const maxApprovalSelector = (state: ApprovalSettingsStore) => state.maxApproval;

export const approveWithoutBalanceDependencies = (isSolana: boolean, isSmartWallet: boolean) => {
  return !isSolana && !isSmartWallet;
};

/**
 * Whether the "Max approval" toggle should be shown.
 * Hidden when ERC20 approval is irrelevant: Solana, smart wallets (EIP-5792 batches
 * always use exact approval), gasless trades (amount is set server-side via permit),
 * and native sell tokens (which don't need approval at all).
 */
export const maxApprovalToggleVisible = ({
  isSolana,
  isSmartWallet,
  isGasless,
  isNativeSellToken,
}: {
  isSolana: boolean;
  isSmartWallet: boolean;
  isGasless: boolean;
  isNativeSellToken: boolean;
}) => {
  return !isSolana && !isSmartWallet && !isGasless && !isNativeSellToken;
};
